import {
  getDoc,
  getDocs,
  collectionGroup,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from 'firebase/storage';
import { auth, db, storage } from './firebase';
import { tenantCollection, tenantDocument } from './tenantService';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_CONTENT_LENGTH = 10000;
const ALLOWED_FILE_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/webp',
  'text/plain',
]);

const isPermissionDeniedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const code = 'code' in error ? String((error as { code?: unknown }).code) : '';
  const message =
    'message' in error ? String((error as { message?: unknown }).message) : '';
  return (
    code === 'permission-denied' ||
    code === 'storage/unauthorized' ||
    message.includes('Missing or insufficient permissions')
  );
};

export interface SubmissionAttachment {
  id: string;
  name: string;
  url: string;
  storagePath: string;
  size: number;
  type: string;
  uploadedAt: Timestamp;
  uploadedBy: string;
}

export interface StudentSubmission {
  id: string;
  teacherId: string;
  tenantId: string;
  assignmentId: string;
  studentId: string;
  studentName: string;
  content: string;
  attachments: SubmissionAttachment[];
  status: 'submitted' | 'graded' | 'returned';
  submittedAt: Date;
  updatedAt: Date;
  createdAt: Date;
  isLate: boolean;
  grade?: number;
  feedback?: string;
  gradedAt?: Date;
}

export interface SubmitAssignmentInput {
  teacherId: string;
  assignmentId: string;
  content: string;
  files: File[];
}

const toDate = (value: unknown): Date => {
  if (value instanceof Date) return value;
  if (
    value &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as { toDate?: unknown }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  const parsed = new Date(value as string | number);
  return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
};

const sanitizeFileName = (name: string) =>
  name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-120) || 'attachment';

const validateFiles = (files: File[], existingCount: number) => {
  if (files.length + existingCount > MAX_FILES) {
    throw new Error(`يمكن إرفاق ${MAX_FILES} ملفات كحد أقصى.`);
  }
  files.forEach(file => {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`حجم الملف ${file.name} يتجاوز 10 ميجابايت.`);
    }
    if (!ALLOWED_FILE_TYPES.has(file.type)) {
      throw new Error(`نوع الملف ${file.name} غير مدعوم.`);
    }
  });
};

const mapSubmission = (id: string, data: Record<string, any>): StudentSubmission => ({
  id,
  teacherId: data.teacherId || data.tenantId,
  tenantId: data.tenantId || data.teacherId,
  assignmentId: data.assignmentId,
  studentId: data.studentId,
  studentName: data.studentName || 'طالب',
  content: data.content || '',
  attachments: Array.isArray(data.attachments) ? data.attachments : [],
  status: data.status || 'submitted',
  submittedAt: toDate(data.submittedAt),
  updatedAt: toDate(data.updatedAt),
  createdAt: toDate(data.createdAt),
  isLate: Boolean(data.isLate),
  grade: data.grade,
  feedback: data.feedback,
  gradedAt: data.gradedAt ? toDate(data.gradedAt) : undefined,
});

class AssignmentSubmissionService {
  async getStudentSubmissions(
    studentId: string,
    teacherIds?: string[]
  ): Promise<StudentSubmission[]> {
    if (!teacherIds || teacherIds.length === 0) {
      const snapshot = await getDocs(
        query(
          collectionGroup(db, 'submissions'),
          where('studentId', '==', studentId)
        )
      );
      return snapshot.docs.map(item => mapSubmission(item.id, item.data()));
    }

    const snapshots = await Promise.all(
      teacherIds.map(teacherId =>
        getDocs(
          query(
            tenantCollection(teacherId, 'submissions'),
            where('studentId', '==', studentId)
          )
        )
      )
    );

    return snapshots.flatMap(snapshot =>
      snapshot.docs.map(item => mapSubmission(item.id, item.data()))
    );
  }

  async submitAssignment(input: SubmitAssignmentInput): Promise<StudentSubmission> {
    const currentUser = auth.currentUser;
    const studentId = currentUser?.uid;
    if (!studentId) throw new Error('يجب تسجيل الدخول لتسليم الواجب.');

    const assignmentRef = tenantDocument(
      input.teacherId,
      'assignments',
      input.assignmentId
    );
    const assignmentSnapshot = await getDoc(assignmentRef);
    if (!assignmentSnapshot.exists()) throw new Error('الواجب غير موجود.');

    const assignment = assignmentSnapshot.data();
    if (
      !Array.isArray(assignment.studentIds) ||
      !assignment.studentIds.includes(studentId)
    ) {
      throw new Error('هذا الواجب غير مخصص لحساب الطالب الحالي.');
    }

    const dueDate = toDate(assignment.dueDate);
    const isLate = dueDate.getTime() > 0 && dueDate.getTime() < Date.now();
    const allowsLate =
      assignment.allowLateSubmissions ?? assignment.allowLateSubmission ?? false;
    if (isLate && !allowsLate) {
      throw new Error('انتهى موعد التسليم ولا يسمح المدرس بالتسليم المتأخر.');
    }

    const content = input.content.trim();
    if (!content && input.files.length === 0) {
      throw new Error('أضف إجابة نصية أو ملفًا واحدًا على الأقل.');
    }
    if (content.length > MAX_CONTENT_LENGTH) {
      throw new Error('نص التسليم يتجاوز الحد الأقصى وهو 10000 حرف.');
    }

    const submissionId = `${input.assignmentId}_${studentId}`;
    const submissionRef = tenantDocument(
      input.teacherId,
      'submissions',
      submissionId
    );
    const existingSnapshot = await getDoc(submissionRef);
    const existing = existingSnapshot.exists() ? existingSnapshot.data() : null;
    if (existing?.status === 'graded') {
      throw new Error('لا يمكن تعديل التسليم بعد اعتماد الدرجة.');
    }

    const existingAttachments = Array.isArray(existing?.attachments)
      ? (existing?.attachments as SubmissionAttachment[])
      : [];
    validateFiles(input.files, existingAttachments.length);

    const uploadedAttachments: SubmissionAttachment[] = [];
    try {
      for (const [index, file] of input.files.entries()) {
        const attachmentId = `${Date.now()}-${index}`;
        const storagePath = `teachers/${input.teacherId}/submissions/${studentId}/${submissionId}/${attachmentId}-${sanitizeFileName(file.name)}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file, {
          contentType: file.type,
          customMetadata: {
            teacherId: input.teacherId,
            studentId,
            assignmentId: input.assignmentId,
            submissionId,
          },
        });
        uploadedAttachments.push({
          id: attachmentId,
          name: file.name,
          url: await getDownloadURL(storageRef),
          storagePath,
          size: file.size,
          type: file.type,
          uploadedAt: Timestamp.now(),
          uploadedBy: studentId,
        });
      }

      const now = serverTimestamp();
      const data = {
        teacherId: input.teacherId,
        tenantId: input.teacherId,
        assignmentId: input.assignmentId,
        studentId,
        studentName:
          currentUser.displayName || currentUser.email || 'طالب',
        content,
        attachments: [...existingAttachments, ...uploadedAttachments],
        status: 'submitted' as const,
        submittedAt: now,
        updatedAt: now,
        createdAt: existing?.createdAt || now,
        isLate,
      };

      await setDoc(submissionRef, data, { merge: false });
      const savedSnapshot = await getDoc(submissionRef);
      return mapSubmission(submissionId, savedSnapshot.data() || data);
    } catch (error) {
      await Promise.allSettled(
        uploadedAttachments.map(attachment =>
          deleteObject(ref(storage, attachment.storagePath))
        )
      );
      if (isPermissionDeniedError(error)) {
        throw new Error(
          'تعذر تسليم الواجب بسبب صلاحيات الحفظ. تأكد أن الواجب مخصص لك وأن موعد التسليم ما زال مسموحًا.'
        );
      }
      throw error;
    }
  }
}

export const assignmentSubmissionService = new AssignmentSubmissionService();
export default assignmentSubmissionService;
